import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Lock, Sun, Moon, Globe } from 'lucide-react';

import AuthLayout from '../../components/layout/AuthLayout';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import i18n from '../../i18n/config';

export default function LoginPage() {
    const { t } = useTranslation();
    const { isDark, toggleTheme } = useTheme();
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [serverError, setServerError] = useState('');

    const { register, handleSubmit, formState: { errors } } = useForm({ mode: 'onTouched' });

    const fromState = location.state?.from;
    const from = fromState?.pathname ? `${fromState.pathname}${fromState.search || ''}` : null;

    function switchLanguage(lang) {
        i18n.changeLanguage(lang);
        localStorage.setItem('lang', lang);
    }

    async function onSubmit(data) {
        setIsLoading(true);
        setServerError('');
        try {
            const user = await login(data.login, data.password);
            let redirect = from;
            if (!redirect) {
                if (user.role === 'doc_user') {
                    redirect = user.default_page === 'panel' ? '/panel' : '/admin/docs';
                } else if (user.role === 'gantt_user') {
                    redirect = user.default_page === 'panel' ? '/panel' : '/admin/gantt';
                } else if (user.role === 'admin' && user.default_page !== 'panel') {
                    redirect = '/admin/users';
                } else {
                    redirect = '/panel';
                }
            }
            navigate(redirect, { replace: true });
        } catch (err) {
            setServerError(err.message || t('errors.invalid_credentials'));
        } finally {
            setIsLoading(false);
        }
    }

    const currentLang = i18n.language;

    return (
        <AuthLayout>
            {/* Top-right controls */}
            <div className="flex justify-end items-center gap-2 mb-8">
                <div className="flex rounded-lg overflow-hidden border border-border">
                    {[{ code: 'ru', label: t('lang_ru') }, { code: 'uz', label: t('lang_uz') }, { code: 'en', label: t('lang_en') }].map(({ code: lang, label }) => (
                        <button
                            key={lang}
                            onClick={() => switchLanguage(lang)}
                            className={`px-3 py-1.5 text-xs font-semibold transition-colors ${currentLang === lang ? 'bg-brand text-inverse' : 'text-muted hover:bg-surface-2'
                                }`}
                        >
                            <Globe className="w-3 h-3 inline mr-1 mb-0.5" />
                            {label}
                        </button>
                    ))}
                </div>
                <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={toggleTheme}
                    className="p-2 rounded-lg border border-border text-muted hover:bg-surface-2 transition-colors"
                    id="theme-toggle-btn"
                >
                    {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </motion.button>
            </div>

            {/* Title */}
            <motion.h2
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl font-extrabold text-primary mb-2"
            >
                {t('login_title')}
            </motion.h2>
            <p className="text-sm text-muted mb-7">{t('welcome_subtitle')}</p>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
                <Input
                    label={t('login_input')}
                    icon={User}
                    index={0}
                    error={errors.login?.message}
                    register={register('login', { required: t('errors.login_required') })}
                />
                <Input
                    label={t('password')}
                    icon={Lock}
                    index={1}
                    type="password"
                    error={errors.password?.message}
                    register={register('password', { required: t('errors.password_required') })}
                />

                {serverError && (
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        className="text-sm text-error text-center font-medium">
                        {serverError}
                    </motion.p>
                )}

                <Button type="submit" isLoading={isLoading} id="login-btn">
                    {t('login')}
                </Button>
            </form>
        </AuthLayout>
    );
}
